import { QUERY_KEYS } from '@/constants';
import { type COMMUNITY_KIND, COMMUNITY_SOURCE } from '@/types';

type CommunityListCardsQueryKeyParams = {
  churchId: string;
  listId: string;
};

type CommunityCardsQueryKeyParams = {
  churchId: string;
  kind: COMMUNITY_KIND;
  source?: COMMUNITY_SOURCE;
};

type CommunityListsQueryKeyParams = {
  churchId: string;
  kind: COMMUNITY_KIND;
  source?: COMMUNITY_SOURCE;
};

type CommunityCaminoSyncStatusQueryKeyParams = {
  churchId: string;
};

type CommunityListCaminoSyncStatusQueryKeyParams = {
  churchId: string;
  listId: string;
};

type CommunityImportCurrentQueryKeyParams = {
  churchId: string;
  listId: string;
};

type CommunityImportStatusQueryKeyParams = {
  churchId: string;
  listId: string;
  processId: string;
};

type InvalidateCommunityListCardsForChurchParams = {
  queryClient: {
    invalidateQueries: (filters: { predicate: (query: { queryKey: readonly unknown[] }) => boolean }) => void;
  };
  churchId: string;
};

type InvalidateCommunityDataForChurchParams = {
  queryClient: {
    invalidateQueries: (filters: { predicate: (query: { queryKey: readonly unknown[] }) => boolean }) => void;
  };
  churchId: string;
};

export const getCommunityListCardsQueryKey = ({ churchId, listId }: CommunityListCardsQueryKeyParams) =>
  [QUERY_KEYS.COMMUNITY_LIST_CARDS, churchId, listId] as const;

export const getCommunityCardsQueryKey = ({
  churchId,
  kind,
  source = COMMUNITY_SOURCE.MANUAL,
}: CommunityCardsQueryKeyParams) => [QUERY_KEYS.COMMUNITY_CARDS, churchId, kind, source] as const;

export const getCommunityListsQueryKey = ({
  churchId,
  kind,
  source = COMMUNITY_SOURCE.MANUAL,
}: CommunityListsQueryKeyParams) => `${QUERY_KEYS.COMMUNITY_LISTS}-${churchId}-${kind}-${source}`;

export const getCommunityCaminoSyncStatusQueryKey = ({ churchId }: CommunityCaminoSyncStatusQueryKeyParams) =>
  [QUERY_KEYS.COMMUNITY_CAMINO_SYNC_STATUS, churchId] as const;

export const getCommunityListCaminoSyncStatusQueryKey = ({
  churchId,
  listId,
}: CommunityListCaminoSyncStatusQueryKeyParams) =>
  [QUERY_KEYS.COMMUNITY_LIST_CAMINO_SYNC_STATUS, churchId, listId] as const;

export const getCommunityImportCurrentQueryKey = ({ churchId, listId }: CommunityImportCurrentQueryKeyParams) =>
  [QUERY_KEYS.COMMUNITY_IMPORT_CURRENT, churchId, listId] as const;

export const getCommunityImportStatusQueryKey = ({
  churchId,
  listId,
  processId,
}: CommunityImportStatusQueryKeyParams) => [QUERY_KEYS.COMMUNITY_IMPORT_STATUS, churchId, listId, processId] as const;

// usePagination stores keys as [queryKey, filters], so invalidation must target [queryKey].
export const getPaginatedQueryInvalidationKey = (queryKey: readonly unknown[] | string) => [queryKey];

export const invalidateCommunityListCardsForChurch = ({
  queryClient,
  churchId,
}: InvalidateCommunityListCardsForChurchParams) => {
  queryClient.invalidateQueries({
    predicate: (query) => {
      const paginationKey = query.queryKey[0];

      if (!Array.isArray(paginationKey)) {
        return false;
      }

      return paginationKey[0] === QUERY_KEYS.COMMUNITY_LIST_CARDS && paginationKey[1] === churchId;
    },
  });
};

export const invalidateCommunityListsForChurch = ({
  queryClient,
  churchId,
}: InvalidateCommunityDataForChurchParams) => {
  queryClient.invalidateQueries({
    predicate: (query) => {
      const rootKey = query.queryKey[0];
      return typeof rootKey === 'string' && rootKey.startsWith(`${QUERY_KEYS.COMMUNITY_LISTS}-${churchId}`);
    },
  });
};

export const invalidateCommunityDataForChurch = ({ queryClient, churchId }: InvalidateCommunityDataForChurchParams) => {
  invalidateCommunityListsForChurch({ queryClient, churchId });
  queryClient.invalidateQueries({
    predicate: (query) => {
      if (
        Array.isArray(query.queryKey) &&
        (query.queryKey[0] === QUERY_KEYS.COMMUNITY_LIST || query.queryKey[0] === QUERY_KEYS.COMMUNITY_CARD) &&
        query.queryKey[1] === churchId
      ) {
        return true;
      }

      const rootKey = query.queryKey[0];

      if (!Array.isArray(rootKey)) {
        return false;
      }

      const [resource, resourceChurchId] = rootKey;

      if (resourceChurchId !== churchId) {
        return false;
      }

      return resource === QUERY_KEYS.COMMUNITY_CARDS || resource === QUERY_KEYS.COMMUNITY_LIST_CARDS;
    },
  });
};
